import {
    useEffect
} from "react";
import {
    ActivityIndicator
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    PageContainer
} from "ncore-ui-kit";
import {
    useGlobalState
} from "../../../context";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import type RootStackParamList from "../../../types/navigation";
import type CommonStackParamList from "../../../types/navigation/common";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import OneSignalService from "../../../services/OneSignalService";
import {
    RESTService
} from "../../../services/restAPI";
import {
    storage
} from "../../../storage";

const NewsRouter = () => {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const route = useRoute<RouteProp<CommonStackParamList, "NewsRouter">>();

    const {
        colors
    } = NCoreUIKitTheme.useContext();

    const [
        globalState,
        setGlobalState
    ] = useGlobalState();

    useEffect(() => {
        if(!globalState.isAppReady) {
            const refreshToken = storage.getString("refreshToken");

            if(refreshToken) {
                RESTService.action("Me", null)
                    .then((res) => {
                        const userInfo = res.payload;

                        setGlobalState({
                            ...globalState,
                            isGuest: false,
                            isAppReady: true,
                            user: {
                                profilePhotoURL: userInfo.profilePhoto,
                                companyTitle: userInfo.companyTitle,
                                companyName: userInfo.companyName,
                                phoneNumber: userInfo.phoneNumber,
                                firstName: userInfo.firstName,
                                lastName: userInfo.lastName,
                                fullName: userInfo.fullName,
                                userType: userInfo.userType,
                                company: userInfo.company,
                                userID: userInfo.userID,
                                mail: userInfo.mail
                            }
                        });

                        OneSignalService.login(res.payload.userID);
                        OneSignalService.setTags({
                            userID: res.payload.userID
                        });
                    })
                    .catch(() => {
                        OneSignalService.logout();
                        storage.clearAll();
                        setGlobalState({
                            ...globalState,
                            isGuest: true,
                            isAppReady: true
                        });
                    });
            } else {
                setGlobalState({
                    ...globalState,
                    isGuest: true,
                    isAppReady: true
                });
            }
        }
    }, [
        globalState.isAppReady
    ]);

    useEffect(() => {
        if(!globalState.isAppReady) {
            return;
        }

        const refreshToken = storage.getString("refreshToken");
        const isGuestUser = globalState.isGuest || !refreshToken;
        const newsID = route.params?.newsID;

        if(!isGuestUser) {
            if(newsID) {
                navigation.reset({
                    index: 0,
                    routes: [
                        {
                            name: "App",
                            state: {
                                routes: [
                                    {
                                        name: "MainTabs"
                                    },
                                    {
                                        name: "NewsDetail",
                                        params: {
                                            newsID: newsID
                                        }
                                    }
                                ]
                            }
                        }
                    ]
                });
                return;
            }

            navigation.reset({
                index: 0,
                routes: [
                    {
                        name: "App",
                        state: {
                            routes: [
                                {
                                    name: "MainTabs",
                                    state: {
                                        routes: [
                                            {
                                                name: "HomeTab",
                                                state: {
                                                    routes: [
                                                        {
                                                            name: "Home"
                                                        },
                                                        {
                                                            name: "News"
                                                        }
                                                    ]
                                                }
                                            }
                                        ]
                                    }
                                }
                            ]
                        }
                    }
                ]
            });
            return;
        }

        if(!globalState.isGuest) {
            setGlobalState({
                ...globalState,
                isGuest: true,
                isAppReady: true
            });
            return;
        }

        if(newsID) {
            navigation.reset({
                index: 0,
                routes: [
                    {
                        name: "Guest",
                        state: {
                            routes: [
                                {
                                    name: "MainTabs"
                                },
                                {
                                    name: "NewsDetail",
                                    params: {
                                        newsID: newsID
                                    }
                                }
                            ]
                        }
                    }
                ]
            });
            return;
        }

        navigation.reset({
            index: 0,
            routes: [
                {
                    name: "Guest",
                    state: {
                        routes: [
                            {
                                name: "MainTabs",
                                state: {
                                    routes: [
                                        {
                                            name: "HomeTab",
                                            state: {
                                                routes: [
                                                    {
                                                        name: "Home"
                                                    },
                                                    {
                                                        name: "News"
                                                    }
                                                ]
                                            }
                                        }
                                    ]
                                }
                            }
                        ]
                    }
                }
            ]
        });
    }, [
        globalState.isAppReady,
        globalState.isGuest,
        navigation,
        route
    ]);

    return <PageContainer
        style={[
            stylesheet.container,
            stylesheet.loadingContainer
        ]}
        backgroundColor="default"
    >
        <ActivityIndicator
            color={colors.content.text.high}
            size="large"
        />
    </PageContainer>;
};

export default NewsRouter;
